import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { PaginatedResponse, PressReleaseLight } from '@finance-lab/utils/types'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { DataGridPro } from '@mui/x-data-grid-pro/DataGridPro'
import { PressReleaseApi } from '../../../api/PressReleasesApi'
import { GridColDef } from '@mui/x-data-grid/models'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { useAppLocale } from '../../../i18n/localeUtils'
import { PressReleaseRelatedTransactionsTags } from '../../../components/entities/PressReleaseRelatedTransactionsTags'
import { GridApiPro } from '@mui/x-data-grid-pro/models'
import { Link } from 'react-router'
import { getPressReleaseLink } from '../../../utils/get-press-release-link'
import { PressReleasesFilter } from '../../components/press-releases/PressReleasesFilter'
import { useFilterSettings, useResetPaginationOnFilterChange } from '../../components/press-releases/utils'
import { CompanySelect } from '../../../components/entities/CompanySelect'
import { useQuery, useUpdateQuery } from '../../../hooks/useQuery'
import CardContent from '@mui/material/CardContent'
import Card from '@mui/material/Card'
import { gridClasses } from '@mui/x-data-grid-pro'

const PAGE_SIZE = 100
const pressReleaseApi = new PressReleaseApi()

type PressReleaseRow = Omit<PressReleaseLight, 'body'>

const getRowLink = (row: PressReleaseRow) => getPressReleaseLink(row.companyId, row.id)

const PressReleaseCellLink = ({ row, children }: { row: PressReleaseRow; children: React.ReactNode }) => (
    <Link
        to={getRowLink(row)}
        style={{
            alignItems: 'center',
            color: 'inherit',
            display: 'flex',
            height: '100%',
            textDecoration: 'none',
            width: '100%',
        }}
    >
        {children}
    </Link>
)

export const PMCompanyView = () => {
    const query = useQuery()
    const updateQuery = useUpdateQuery()
    const { language: locale, intlDateLocale } = useAppLocale()
    const timeFormatter = useMemo(
        () => new Intl.DateTimeFormat(intlDateLocale, { hour: '2-digit', minute: '2-digit', hour12: false }),
        [intlDateLocale],
    )
    const companyId = query.get('companyId') ?? ''
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<Omit<PressReleaseLight, 'body'>>>({
        totalCount: 0,
        data: [],
    })
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: PAGE_SIZE,
    })
    const apiRef = useRef<GridApiPro | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const { endDate, searchValue, contentType } = useFilterSettings()
    useResetPaginationOnFilterChange(setPaginationModel, { endDate, searchValue, contentType })

    useEffect(() => {
        if (!companyId) {
            setPageInfo({ data: [], totalCount: 0 })
            setIsLoading(false)
            return
        }

        const fetchData = async () => {
            const { data, totalCount } = await pressReleaseApi.allPressReleases(paginationModel, {
                companyId,
                maxPublicationDate: endDate ?? undefined,
                contentType,
                search: searchValue,
            })

            setPageInfo({ data: data.map(({ body, ...pr }) => pr), totalCount })
            setIsLoading(false)
        }

        void fetchData()
        setIsLoading(true)
    }, [companyId, contentType, endDate, paginationModel, searchValue])

    const columns: GridColDef<PressReleaseRow>[] = [
        {
            field: 'relatedTransactions',
            display: 'flex',
            align: 'left',
            sortable: false,
            headerName: '',
            width: 120,
            renderCell: ({ row }) => (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '100%',
                        position: 'relative',
                        width: '100%',
                    }}
                >
                    <Link to={getRowLink(row)} aria-hidden tabIndex={-1} style={{ inset: 0, position: 'absolute' }} />
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <PressReleaseRelatedTransactionsTags relatedTransactions={row.relatedTransactions} />
                    </Box>
                </Box>
            ),
        },
        {
            headerName: 'Tidpunkt',
            field: 'publishedAt',
            width: 140,
            renderCell: ({ row }) => {
                return (
                    <PressReleaseCellLink row={row}>
                        <Box sx={{ width: '80px' }}>{formatDateYYYYMMDD(row.publishedAt, { locale })}</Box>
                        <Box>{timeFormatter.format(new Date(row.publishedAt))}</Box>
                    </PressReleaseCellLink>
                )
            },
        },
        {
            headerName: 'Rubrik',
            field: 'title',
            minWidth: 360,
            flex: 1,
            renderCell: ({ row }) => <PressReleaseCellLink row={row}>{row.title}</PressReleaseCellLink>,
        },
    ]

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 'calc(100dvh - 64px - 72px)',
            }}
        >
            <Helmet title='PM Bolag' />
            <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 4, mb: 2 }}>
                <Typography variant='h3'>PM Bolag</Typography>
                {companyId ? (
                    <Typography variant='body1'>
                        <Link to={`/companies/${companyId}`}>[Länk till bolagsvy]</Link>
                    </Typography>
                ) : null}
            </Box>
            <Box sx={{ maxWidth: 480, marginBottom: 2 }}>
                <CompanySelect
                    companyId={companyId || undefined}
                    onSelectCompany={(company) => updateQuery('companyId', company?.id ?? null)}
                />
            </Box>
            <PressReleasesFilter />
            <Divider sx={{ marginY: 2 }} />
            {!companyId ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'center',
                        minHeight: 0,
                    }}
                >
                    <Typography color='text.secondary'>Välj bolag för att visa pressmeddelanden.</Typography>
                </Box>
            ) : (
                <Box>
                    <Card sx={{ mb: 6 }}>
                        <CardContent>
                            <DataGridPro
                                apiRef={apiRef}
                                columns={columns}
                                columnHeaderHeight={52}
                                density='compact'
                                filterMode='server'
                                loading={isLoading}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[PAGE_SIZE]}
                                paginationModel={paginationModel}
                                paginationMode='server'
                                pagination
                                rowHeight={48}
                                rows={pageInfo.data}
                                rowCount={pageInfo.totalCount}
                                sx={{
                                    ...disableSelectedSellOutlineStyle,
                                    '& .MuiDataGrid-cell': {
                                        alignItems: 'center',
                                        display: 'flex',
                                        py: 1,
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        py: 0,
                                    },
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        </CardContent>
                    </Card>
                </Box>
            )}
        </Box>
    )
}
